import { notImplemented, mod97CheckSum } from "../utils/general-helper";
import { keepDigits } from "../utils/string-helper";
import { IRegionFunctions, ParsedAccount } from "./region-functions";

const regionFunctionsSE = (): IRegionFunctions => {
	const accounNrExpression = RegExp(/^(\d{11,14})$/);
	const accounNrExpressionExtraDigit = RegExp(/^(\d{12}|\d{15})$/);
	const validPhoneNrExpression = RegExp(/^(\+46|0)\d{1,3}[- ]?\d{3}[- ]?\d{2}[- ]?\d{2}$/);
	const basicIbanStructureRegEx = RegExp(/^(?=.{15,34}$)[A-Z]{2}[A-Z0-9]+$/);
	const clearingNumberLength = 4;
	const clearingNumberLengthLong = 5;
	const accountNumberMaxLength = 14;
	const accountNumberMaxLengthLong = 15;

	const validPhoneNumber = (phoneNumber: string): boolean => validPhoneNrExpression.test(phoneNumber);

	const formatPhoneNumber = (_phoneNumber: string): string => {
		return notImplemented();
	};

	const validateAccountNumber = (accountNumber: string): boolean => {
		accountNumber = accountNumber.replace(/\D/g, "");
		if (accountNumber.startsWith("8")) return accounNrExpressionExtraDigit.test(accountNumber);
		return accounNrExpression.test(accountNumber);
	};

	const getClearingLength = (clearing: string) =>
		clearing.startsWith("8") ? clearingNumberLengthLong : clearingNumberLength;

	const getBankAccountMeta = (accountNrIncludingClearing: string) => {
		const valueWithOnlyNumbers = keepDigits(accountNrIncludingClearing);
		const clearingLength = getClearingLength(valueWithOnlyNumbers);
		if (clearingLength === clearingNumberLengthLong) {
			return {
				clearingNumberLength: clearingLength,
				accountNumberMaxLength: accountNumberMaxLengthLong,
			};
		}
		return {
			clearingNumberLength: clearingLength,
			accountNumberMaxLength: accountNumberMaxLength,
		};
	};

	const parseAccountNumber = (accountNrIncClearing: string): ParsedAccount => {
		const clrnumLength = getClearingLength(accountNrIncClearing);

		const [clearingNr, accountNr] = accountNrIncClearing.includes("-")
			? accountNrIncClearing.split("-")
			: [accountNrIncClearing.slice(0, clrnumLength), accountNrIncClearing.slice(clrnumLength)];

		return { clearingNumber: clearingNr, accountNumber: accountNr };
	};

	const formatAccountNumber = (clearingNumber: string, accountNr: string) => {
		const clrnumLength = getClearingLength(clearingNumber);
		if ((clearingNumber + accountNr).length <= clrnumLength) return clearingNumber;
		return `${clearingNumber} ${accountNr}`;
	};

	const validateIban = (IBAN: string, allowedCountryCodes?: string): boolean => {
		if (IBAN.length < 1) return true;
		if (allowedCountryCodes && allowedCountryCodes !== "") {
			const ibanCountryCode = IBAN.substring(0, 2);
			if (!allowedCountryCodes.split(",").some((countryCode) => countryCode === ibanCountryCode))
				return false;
		}
		IBAN = IBAN.replace(/[\s]+/g, "").toUpperCase();
		if (!basicIbanStructureRegEx.test(IBAN)) return false;

		const rearrangedIban = IBAN.slice(4) + IBAN.slice(0, 4);
		const convertedIban = rearrangedIban.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString());

		return mod97CheckSum(convertedIban);
	};

	return {
		validateAccountNumber,
		validPhoneNumber,
		formatPhoneNumber,
		parseAccountNumber,
		validateIban,
		formatAccountNumber,
		bankAccountMeta: getBankAccountMeta,
	};
};

type RegionFunctionsSEType = ReturnType<typeof regionFunctionsSE>;

export const SERegionFunctions: RegionFunctionsSEType = regionFunctionsSE();
